import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Skeleton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { Add, Refresh, CheckCircle } from '@mui/icons-material';
import EditIcon from '@mui/icons-material/Edit';
import { toast } from 'react-toastify';
import { AppDispatch, RootState } from '../../../store/store';
import {
  fetchInvoices,
  generateInvoice,
  markInvoicePaid,
  updateInvoice,
  fetchTenants,
  fetchSubscriptionPlans,
} from '../../../store/slices/superAdminSlice';
import {
  GenerateInvoice,
  PaymentStatusLabels,
  PaymentStatusColors,
} from '../../../types/superAdminTypes';

type MoneyErrorState = {
  amount: string;
  taxAmount: string;
};

type GenerateRequiredErrorState = {
  tenantId: string;
  subscriptionPlanId: string;
  billingPeriodStart: string;
  billingPeriodEnd: string;
};

const emptyMoneyErrors: MoneyErrorState = { amount: '', taxAmount: '' };
const emptyGenerateRequiredErrors: GenerateRequiredErrorState = {
  tenantId: '',
  subscriptionPlanId: '',
  billingPeriodStart: '',
  billingPeriodEnd: '',
};
const moneyInputPattern = /^(?:\d+|\d+\.\d{0,2}|\.\d{1,2})$/;

const getMoneyValidationError = (rawValue: string, label: 'Amount' | 'Tax') => {
  const value = rawValue.trim();
  if (!value) return '';
  if (value.includes('-') || Number(value) < 0) return `${label} cannot be negative.`;
  if (value.includes('.') && value.split('.')[1]?.length > 2) return `${label} can have at most 2 decimal places.`;
  if (!moneyInputPattern.test(value) || Number.isNaN(Number(value))) return `${label} must be numeric.`;
  return '';
};

const BillingInvoicePage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { invoices, tenants, subscriptionPlans, loading, error } = useSelector((state: RootState) => state.superAdmin);

  const [paymentStatusFilter, setPaymentStatusFilter] = useState<number | ''>('');
  const [tenantFilter, setTenantFilter] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState<GenerateInvoice>({ tenantId: '', subscriptionPlanId: '', billingPeriodStart: '', billingPeriodEnd: '', amount: 0, taxAmount: 0 });
  const [formErrors, setFormErrors] = useState<MoneyErrorState>(emptyMoneyErrors);
  const [generateRequiredErrors, setGenerateRequiredErrors] = useState<GenerateRequiredErrorState>(emptyGenerateRequiredErrors);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<GenerateInvoice> & { id?: string; paymentStatus?: number }>({});
  const [editFormErrors, setEditFormErrors] = useState<MoneyErrorState>(emptyMoneyErrors);
  const billableTenants = useMemo(
    () => tenants.filter((tenant) => tenant.isActive && !tenant.isSuspended),
    [tenants]
  );
  const selectedGenerateTenant = tenants.find((tenant) => tenant.id === form.tenantId);
  const selectedEditTenant = tenants.find((tenant) => tenant.id === editForm.tenantId);
  const hasGenerateMoneyErrors = Boolean(formErrors.amount || formErrors.taxAmount);
  const hasEditMoneyErrors = Boolean(editFormErrors.amount || editFormErrors.taxAmount);
  const canGenerateInvoice =
    form.amount >= 0 &&
    (form.taxAmount ?? 0) >= 0 &&
    !hasGenerateMoneyErrors &&
    (!selectedGenerateTenant || (selectedGenerateTenant.isActive && !selectedGenerateTenant.isSuspended));
  const canUpdateInvoice =
    Boolean(editForm.id) &&
    Boolean(editForm.subscriptionPlanId) &&
    Boolean(editForm.billingPeriodStart) &&
    (editForm.amount ?? 0) >= 0 &&
    (editForm.taxAmount ?? 0) >= 0 &&
    !hasEditMoneyErrors &&
    editForm.paymentStatus !== 1 &&
    (!selectedEditTenant || (selectedEditTenant.isActive && !selectedEditTenant.isSuspended));

  const fetchData = useCallback(() => {
    dispatch(fetchInvoices({
      tenantId: tenantFilter || undefined,
      paymentStatus: paymentStatusFilter !== '' ? paymentStatusFilter : undefined,
    }));
  }, [dispatch, tenantFilter, paymentStatusFilter]);

  useEffect(() => {
    fetchData();
    dispatch(fetchTenants({}));
    dispatch(fetchSubscriptionPlans());
  }, [fetchData, dispatch]);

  const validateGenerateRequiredFields = () => {
    const missingFields = [
      {
        keys: ['tenantId'] as const,
        message: 'Tenant is required.',
        missing: !form.tenantId,
      },
      {
        keys: ['subscriptionPlanId'] as const,
        message: 'Subscription Plan is required.',
        missing: !form.subscriptionPlanId,
      },
      {
        keys: ['billingPeriodStart', 'billingPeriodEnd'] as const,
        message: 'Billing Period is required.',
        missing: !form.billingPeriodStart || !form.billingPeriodEnd,
      },
    ].filter((field) => field.missing);

    if (missingFields.length === 0) {
      setGenerateRequiredErrors(emptyGenerateRequiredErrors);
      return true;
    }

    const nextErrors = { ...emptyGenerateRequiredErrors };
    const message = missingFields.length === 1 ? missingFields[0].message : 'Please enter mandatory fields.';

    missingFields.forEach((field) => {
      field.keys.forEach((key) => {
        nextErrors[key] = missingFields.length === 1 ? field.message : 'Required';
      });
    });

    setGenerateRequiredErrors(nextErrors);
    toast.error(message);
    return false;
  };

  const handleGenerate = async () => {
    if (!validateGenerateRequiredFields()) {
      return;
    }

    if (selectedGenerateTenant?.isSuspended) {
      toast.error('Invoices cannot be generated for suspended tenants.');
      return;
    }

    if (selectedGenerateTenant && !selectedGenerateTenant.isActive) {
      toast.error('Invoices cannot be generated for inactive tenants.');
      return;
    }

    try {
      await dispatch(generateInvoice(form)).unwrap();
      toast.success('Invoice generated successfully');
      setOpenDialog(false);
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Failed to generate invoice');
    }
  };

  const handleOpenGenerateDialog = () => {
    setForm({ tenantId: '', subscriptionPlanId: '', billingPeriodStart: '', billingPeriodEnd: '', amount: 0, taxAmount: 0 });
    setFormErrors(emptyMoneyErrors);
    setGenerateRequiredErrors(emptyGenerateRequiredErrors);
    setOpenDialog(true);
  };

  const handleMarkPaid = async (id: string) => {
    try {
      await dispatch(markInvoicePaid(id)).unwrap();
      toast.success('Invoice marked as paid');
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Failed to mark as paid');
    }
  };

  const computeEndDate = (start: string | undefined, planId: string | undefined) => {
    if (!start || !planId) return '';
    const plan = subscriptionPlans.find((p) => p.id === planId);
    const months = plan?.period ?? 12;
    const d = new Date(start);
    if (Number.isNaN(d.getTime())) return '';
    d.setMonth(d.getMonth() + months);
    return d.toISOString().split('T')[0];
  };

  const handleMoneyKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['e', 'E', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  };

  const handleGenerateMoneyChange =
    (field: keyof MoneyErrorState, label: 'Amount' | 'Tax') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const error = getMoneyValidationError(rawValue, label);
      setFormErrors((previous) => ({ ...previous, [field]: error }));

      if (error) return;

      setForm((previous) => ({ ...previous, [field]: rawValue === '' ? 0 : Number(rawValue) }));
    };

  const handleEditMoneyChange =
    (field: keyof MoneyErrorState, label: 'Amount' | 'Tax') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const rawValue = event.target.value;
      const error = getMoneyValidationError(rawValue, label);
      setEditFormErrors((previous) => ({ ...previous, [field]: error }));

      if (error) return;

      setEditForm((previous) => ({ ...previous, [field]: rawValue === '' ? 0 : Number(rawValue) }));
    };

  const openEditDialog = (inv: any) => {
    const plan = subscriptionPlans.find((p) => p.planName === inv.planName);
    setEditForm({
      id: inv.id,
      tenantId: inv.tenantId,
      paymentStatus: inv.paymentStatus,
      subscriptionPlanId: plan?.id ?? '',
      billingPeriodStart: inv.billingPeriodStart?.split('T')?.[0] ?? '',
      billingPeriodEnd: inv.billingPeriodEnd?.split('T')?.[0] ?? '',
      amount: inv.amount,
      taxAmount: inv.taxAmount ?? 0,
    });
    setEditFormErrors(emptyMoneyErrors);
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (editForm.paymentStatus === 1) {
      toast.error('Paid invoices cannot be modified.');
      return;
    }

    if (selectedEditTenant?.isSuspended) {
      toast.error('Invoices cannot be updated for suspended tenants.');
      return;
    }

    if (selectedEditTenant && !selectedEditTenant.isActive) {
      toast.error('Invoices cannot be updated for inactive tenants.');
      return;
    }

    try {
      if (!editForm.id) throw new Error('Missing invoice id');
      await dispatch(updateInvoice({ id: editForm.id, data: {
        amount: editForm.amount,
        taxAmount: editForm.taxAmount,
        billingPeriodStart: editForm.billingPeriodStart,
        subscriptionPlanId: editForm.subscriptionPlanId,
      } })).unwrap();
      toast.success('Invoice updated successfully');
      setEditDialogOpen(false);
      fetchData();
    } catch (err: any) {
      toast.error(err || 'Failed to update invoice');
    }
  };

  const getInvoiceTenant = (tenantId: string) => tenants.find((tenant) => tenant.id === tenantId);
  const isInvoiceEditDisabled = (tenantId: string, paymentStatus: number) => {
    if (paymentStatus === 1) return true;

    const tenant = getInvoiceTenant(tenantId);
    return Boolean(tenant && (!tenant.isActive || tenant.isSuspended));
  };
  const getInvoiceEditTooltip = (tenantId: string, paymentStatus: number) => {
    if (paymentStatus === 1) return 'Paid invoices cannot be modified.';

    const tenant = getInvoiceTenant(tenantId);
    if (tenant?.isSuspended) return 'Invoices cannot be updated for suspended tenants.';
    if (tenant && !tenant.isActive) return 'Invoices cannot be updated for inactive tenants.';
    return 'Edit';
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1.5, sm: 0 }, mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' } }}>Billing & Invoices</Typography>
          <Typography variant="body2" color="text.secondary">Manage invoices and track payments</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpenGenerateDialog} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', whiteSpace: 'nowrap' }}>
          Generate Invoice
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <Autocomplete
              options={[{ id: '', companyName: 'All' }, ...tenants]}
              getOptionLabel={(option) => option.companyName}
              value={tenants.find((t) => t.id === tenantFilter) || (tenantFilter === '' ? { id: '', companyName: 'All' } : null)}
              onChange={(_, newValue) => setTenantFilter(newValue ? newValue.id : '')}
              renderInput={(params) => (
                <TextField {...params} label="Tenant" size="small" fullWidth />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select value={paymentStatusFilter} label="Payment Status" onChange={(e) => setPaymentStatusFilter(e.target.value as number | '')}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value={0}>Pending</MenuItem>
                <MenuItem value={1}>Paid</MenuItem>
                <MenuItem value={2}>Overdue</MenuItem>
                <MenuItem value={3}>Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button fullWidth variant="outlined" startIcon={<Refresh />} onClick={fetchData}>Refresh</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Invoice #</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Company</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Plan</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Billing Start</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Billing End</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Tax</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>{Array.from({ length: 10 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
              ))
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No invoices found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((inv) => (
                <TableRow key={inv.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={600}>{inv.invoiceNumber}</Typography></TableCell>
                  <TableCell>{inv.tenantName}</TableCell>
                  <TableCell>{inv.planName}</TableCell>
                  <TableCell>
                    {new Date(inv.billingPeriodStart).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </TableCell>

                  <TableCell>
                    {new Date(inv.billingPeriodEnd).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </TableCell>
                  <TableCell>₹{inv.amount.toLocaleString()}</TableCell>
                  <TableCell>₹{(inv.taxAmount ?? 0).toLocaleString()}</TableCell>
                  <TableCell><Typography fontWeight={600}>₹{inv.totalAmount.toLocaleString()}</Typography></TableCell>
                  <TableCell>
                    <Chip label={PaymentStatusLabels[inv.paymentStatus] || 'Unknown'} color={PaymentStatusColors[inv.paymentStatus] || 'default'} size="small" />
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Tooltip title={getInvoiceEditTooltip(inv.tenantId, inv.paymentStatus)}>
                        <span>
                          <IconButton size="small" color="primary" onClick={() => openEditDialog(inv)} disabled={isInvoiceEditDisabled(inv.tenantId, inv.paymentStatus)}><EditIcon fontSize="small" /></IconButton>
                        </span>
                      </Tooltip>
                      {inv.paymentStatus !== 1 && (
                        <Tooltip title="Mark Paid">
                          <IconButton size="small" color="success" onClick={() => handleMarkPaid(inv.id)}><CheckCircle fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Generate Invoice Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={billableTenants}
                getOptionLabel={(option) => option.companyName}
                value={billableTenants.find((t) => t.id === form.tenantId) || null}
                onChange={(_, newValue) => {
                  setForm({ ...form, tenantId: newValue ? newValue.id : '' });
                  setGenerateRequiredErrors((previous) => ({ ...previous, tenantId: '' }));
                }}
                noOptionsText="No active tenants available"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tenant"
                    fullWidth
                    error={Boolean(generateRequiredErrors.tenantId)}
                    helperText={generateRequiredErrors.tenantId}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={subscriptionPlans}
                getOptionLabel={(option) => option.planName}
                value={subscriptionPlans.find((p) => p.id === form.subscriptionPlanId) || null}
                onChange={(_, newValue) => {
                  setForm({
                    ...form,
                    subscriptionPlanId: newValue ? newValue.id : '',
                    billingPeriodEnd: computeEndDate(form.billingPeriodStart, newValue ? newValue.id : undefined),
                  });
                  setGenerateRequiredErrors((previous) => ({
                    ...previous,
                    subscriptionPlanId: '',
                    billingPeriodEnd: '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Subscription Plan"
                    fullWidth
                    error={Boolean(generateRequiredErrors.subscriptionPlanId)}
                    helperText={generateRequiredErrors.subscriptionPlanId}
                  />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Billing Start"
                type="date"
                value={form.billingPeriodStart}
                onChange={(e) => {
                  setForm({ ...form, billingPeriodStart: e.target.value, billingPeriodEnd: computeEndDate(e.target.value, form.subscriptionPlanId) });
                  setGenerateRequiredErrors((previous) => ({
                    ...previous,
                    billingPeriodStart: '',
                    billingPeriodEnd: '',
                  }));
                }}
                InputLabelProps={{ shrink: true }}
                error={Boolean(generateRequiredErrors.billingPeriodStart)}
                helperText={generateRequiredErrors.billingPeriodStart}
              />
            </Grid>
             <Grid size={{ xs: 12, sm: 6 }}>
           <TextField
              fullWidth
              label="Billing End"
              type="date"
              value={form.billingPeriodEnd}
              InputLabelProps={{ shrink: true }}
              onKeyDown={(e) => e.preventDefault()}
              error={Boolean(generateRequiredErrors.billingPeriodEnd)}
              helperText={generateRequiredErrors.billingPeriodEnd}
            />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={form.amount}
                onChange={handleGenerateMoneyChange('amount', 'Amount')}
                onKeyDown={handleMoneyKeyDown}
                error={Boolean(formErrors.amount)}
                helperText={formErrors.amount}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tax (₹)"
                type="number"
                value={form.taxAmount}
                onChange={handleGenerateMoneyChange('taxAmount', 'Tax')}
                onKeyDown={handleMoneyKeyDown}
                error={Boolean(formErrors.taxAmount)}
                helperText={formErrors.taxAmount}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGenerate} disabled={!canGenerateInvoice} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Generate</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Invoice Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Edit Invoice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                options={subscriptionPlans}
                getOptionLabel={(option) => option.planName}
                value={subscriptionPlans.find((p) => p.id === editForm.subscriptionPlanId) || null}
                onChange={(_, newValue) => setEditForm({
                  ...editForm,
                  subscriptionPlanId: newValue ? newValue.id : '',
                  billingPeriodEnd: computeEndDate(editForm.billingPeriodStart as string, newValue ? newValue.id : undefined),
                })}
                renderInput={(params) => (
                  <TextField {...params} label="Subscription Plan" fullWidth />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Billing Start" type="date" value={editForm.billingPeriodStart || ''} onChange={(e) => setEditForm({ ...editForm, billingPeriodStart: e.target.value, billingPeriodEnd: computeEndDate(e.target.value, editForm.subscriptionPlanId) })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth label="Billing End" type="date" value={editForm.billingPeriodEnd || ''} InputLabelProps={{ shrink: true }} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Amount (₹)"
                type="number"
                value={editForm.amount ?? 0}
                onChange={handleEditMoneyChange('amount', 'Amount')}
                onKeyDown={handleMoneyKeyDown}
                error={Boolean(editFormErrors.amount)}
                helperText={editFormErrors.amount}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tax (₹)"
                type="number"
                value={editForm.taxAmount ?? 0}
                onChange={handleEditMoneyChange('taxAmount', 'Tax')}
                onKeyDown={handleMoneyKeyDown}
                error={Boolean(editFormErrors.taxAmount)}
                helperText={editFormErrors.taxAmount}
                inputProps={{ min: 0, step: '0.01' }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate} disabled={!canUpdateInvoice} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingInvoicePage;
